import React, { useEffect, useState } from 'react';
import { Container, Grid, GridItem, Heading, Text } from '@charlietango/ui';
import { FontWeight, TextVariant } from '../../styles/typography';
import RichText from '../../components/RichText/RichText';
import { rem } from '../../styles/rem';
import Image from '../../components/Image/Image';
import Button from '../../components/Button/Button';
import { ButtonVariant } from '../../styles/buttons';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';
import {
  useChosenElectionType,
  useDeclarationType,
  useElectionType,
  usePartyId,
} from '../../hooks/useSearchParams';
import { SearchParamsLike } from '../../utils/search-params';
import { useRouter } from '../../application/Router';
import { useUser } from '../../api/hooks/useUser';
import DialogPageViewModel from './types/DialogPageViewModel';
import {
  computeDialogFlowState,
  DialogFlowMode,
  DialogFlowState,
  DialogFlowKind,
  getButton,
  getCancelButton,
  getDialogImage,
  getDialogText,
} from './dialogPageHelper';
import {
  buildDialogUrl,
  buildPartyDetailsUrl,
} from '../../utils/dialogFlowUrl';
import { useDictionary } from '../../hooks/useDictionary';
import { useParties } from '../../api/hooks/useParties';
import { useParty } from '../../api/hooks/useParty';
import { CollectionState, ElectionType } from '../../api/api-types';
import { SchemaParty } from '../../api/api-schema';
import useAddDeclaration from '../../api/hooks/useAddDeclaration';
import Placeholder from '../../components/Placeholder/Placeholder';
import Modal from '../../components/Modal/Modal';
import { getErrorWithState } from '../../components/StatusMessages/StatusMessageOverlay';
import { usePartyDetailsUrl } from '../../application/PartyDetailsUrlContext';
import { readableHeadingTextWrap } from '../../styles/textWrap';
import { useAuth } from '../../api/AuthProvider';
import { redirectToUrl } from '../../utils/browser-redirect';
import { canConfirmDeclaration as canConfirmUserDeclaration } from '../../components/StatusBar/statusBarHelpers';
import {
  ButtonsSection,
  collectionStateByElectionType,
  StaleActionGuard,
} from './StaleActionGuard';

const fullWidthButtonQuery = '@media (max-width: 47.99em)';
const narrowTabletDialogQuery =
  '@media (min-width: 48em) and (max-width: 51.99em)';
const narrowTabletButtonWidth = {
  width: 'auto',
  [fullWidthButtonQuery]: {
    width: '100%',
  },
};

export type Props = Partial<DialogPageViewModel>;

type DialogQueryFlowContext = {
  flowMode: DialogFlowMode;
  nextInitiateReturnUrl?: string;
  confirmReturnUrlOverride?: string;
  withdrawnPartyId?: string;
  withdrawnPartyName?: string;
};

const getDialogQueryFlowContext = ({
  searchParams,
  declarationType,
  electionType,
  chosenElectionType,
  dialogPageUrl,
  partyId,
}: {
  searchParams?: SearchParamsLike;
  declarationType?: string;
  electionType?: ElectionType;
  chosenElectionType?: 'dk' | 'eu';
  dialogPageUrl: string;
  partyId?: string;
}): DialogQueryFlowContext => {
  const isBothFlow = searchParams?.get('bothFlow') === '1';
  const flowMode = isBothFlow
    ? DialogFlowMode.SupportBothSequence
    : DialogFlowMode.Default;
  const nextConfirmPartyId = searchParams?.get('nextParty') || undefined;
  const withdrawnPartyId = searchParams?.get('withdrawParty') || undefined;
  const withdrawnPartyName =
    searchParams?.get('withdrawPartyName') || undefined;

  const nextInitiateReturnUrl =
    isBothFlow &&
    declarationType === DeclarationActionTypes.initiate &&
    electionType === ElectionType.BothTypes &&
    chosenElectionType === 'dk'
      ? buildDialogUrl(dialogPageUrl, {
          type: DeclarationActionTypes.initiate,
          electionType: ElectionType.BothTypes,
          party: partyId ?? undefined,
          choice: 'eu',
          extraParams: { bothFlow: '1' },
        })
      : undefined;

  const confirmReturnUrlOverride =
    declarationType === DeclarationActionTypes.confirm && nextConfirmPartyId
      ? buildDialogUrl(dialogPageUrl, {
          type: DeclarationActionTypes.confirm,
          electionType: ElectionType.EuropaParlamentsvalg,
          party: nextConfirmPartyId,
        })
      : undefined;

  return {
    flowMode,
    nextInitiateReturnUrl,
    confirmReturnUrlOverride,
    withdrawnPartyId,
    withdrawnPartyName,
  };
};

function RedirectToPartyPage({ url }: { url?: string }) {
  const { replace } = useRouter();

  useEffect(() => {
    if (url) replace(url);
  }, [replace, url]);

  return <Placeholder css={{ height: 600 }} />;
}

const getSigningActionForFlow = (
  flowState: DialogFlowState,
):
  | DeclarationActionTypes.confirm
  | DeclarationActionTypes.withdraw
  | undefined => {
  if (flowState.kind === DialogFlowKind.Confirm) {
    return DeclarationActionTypes.confirm;
  }

  if (
    flowState.kind === DialogFlowKind.Withdraw ||
    flowState.kind === DialogFlowKind.InitiateBothTypesUserHasDeclaration
  ) {
    return DeclarationActionTypes.withdraw;
  }

  return undefined;
};

function ConfirmationSection({
  dkPartyName,
  euPartyName,
  partyIdDk,
  partyIdEu,
  model,
}: {
  dkPartyName: string;
  euPartyName: string;
  partyIdDk?: string;
  partyIdEu?: string;
  model?: Partial<DialogPageViewModel>;
}) {
  const { t } = useDictionary();
  const { pathname } = useRouter();

  return (
    <section
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: rem(40),
        [fullWidthButtonQuery]: {
          alignItems: 'stretch',
        },
      }}
    >
      <div>
        <Text
          as="h3"
          variant={TextVariant.BodyMd}
          sx={{ fontWeight: FontWeight.Bold }}
        >
          {t('dkElection')}
        </Text>
        <Text as="p" variant={TextVariant.BodyMd} sx={{ mb: 6 }}>
          {dkPartyName}
        </Text>
        {partyIdDk ? (
          <Button
            variant={ButtonVariant.Primary}
            href={buildDialogUrl(pathname, {
              type: DeclarationActionTypes.confirm,
              electionType: ElectionType.Folketingsvalg,
              party: partyIdDk,
            })}
            sx={narrowTabletButtonWidth}
          >
            {model?.buttonConfirmForDk}
          </Button>
        ) : null}
      </div>
      <div>
        <Text
          as="h3"
          variant={TextVariant.BodyMd}
          sx={{ fontWeight: FontWeight.Bold }}
        >
          {t('euElection')}
        </Text>
        <Text as="p" variant={TextVariant.BodyMd} sx={{ mb: 6 }}>
          {euPartyName}
        </Text>
        {partyIdEu ? (
          <Button
            variant={ButtonVariant.Primary}
            href={buildDialogUrl(pathname, {
              type: DeclarationActionTypes.confirm,
              electionType: ElectionType.EuropaParlamentsvalg,
              party: partyIdEu,
            })}
            sx={narrowTabletButtonWidth}
          >
            {model?.buttonConfirmForEu}
          </Button>
        ) : null}
      </div>
      {partyIdDk && partyIdEu && model?.buttonConfirmBoth ? (
        <Button
          variant={ButtonVariant.Primary}
          href={buildDialogUrl(pathname, {
            type: DeclarationActionTypes.confirm,
            electionType: ElectionType.Folketingsvalg,
            party: partyIdDk,
            extraParams: { nextParty: partyIdEu },
          })}
          sx={narrowTabletButtonWidth}
        >
          {model?.buttonConfirmBoth}
        </Button>
      ) : null}
    </section>
  );
}

function SupportSection({
  model,
  partyId,
  existingDeclarationElectionType,
  canSupportBoth,
  cancelUrl,
}: {
  model?: Partial<DialogPageViewModel>;
  partyId: string;
  existingDeclarationElectionType?: ElectionType;
  canSupportBoth: boolean;
  cancelUrl?: string;
}) {
  const { pathname } = useRouter();
  const dialogPageUrl = pathname;
  const initiateDkUrl = buildDialogUrl(dialogPageUrl, {
    type: DeclarationActionTypes.initiate,
    electionType: ElectionType.Folketingsvalg,
    party: partyId,
  });
  const initiateEuUrl = buildDialogUrl(dialogPageUrl, {
    type: DeclarationActionTypes.initiate,
    electionType: ElectionType.EuropaParlamentsvalg,
    party: partyId,
  });
  const initiateBothChoiceDkUrl = buildDialogUrl(dialogPageUrl, {
    type: DeclarationActionTypes.initiate,
    electionType: ElectionType.BothTypes,
    party: partyId,
    choice: 'dk',
  });
  const initiateBothChoiceEuUrl = buildDialogUrl(dialogPageUrl, {
    type: DeclarationActionTypes.initiate,
    electionType: ElectionType.BothTypes,
    party: partyId,
    choice: 'eu',
  });
  const initiateBothFlowUrl = buildDialogUrl(dialogPageUrl, {
    type: DeclarationActionTypes.initiate,
    electionType: ElectionType.BothTypes,
    party: partyId,
    choice: 'dk',
    extraParams: { bothFlow: '1' },
  });

  return (
    <section sx={{ mt: rem(24) }}>
      <div
        sx={{
          display: 'flex',
          flexDirection: ['column'],
          alignItems: ['stretch', 'start'],
          gap: rem(16),
          [fullWidthButtonQuery]: {
            alignItems: 'stretch',
          },
          '@media (min-width: 40em)': {
            alignItems: 'flex-start',
          },
        }}
      >
        <Button
          variant={ButtonVariant.Primary}
          sx={narrowTabletButtonWidth}
          href={
            existingDeclarationElectionType === ElectionType.Folketingsvalg
              ? initiateBothChoiceDkUrl
              : initiateDkUrl
          }
        >
          {model?.buttonSupportForDk}
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          sx={narrowTabletButtonWidth}
          href={
            existingDeclarationElectionType ===
            ElectionType.EuropaParlamentsvalg
              ? initiateBothChoiceEuUrl
              : initiateEuUrl
          }
        >
          {model?.buttonSupportForEu}
        </Button>
        {canSupportBoth ? (
          <Button
            variant={ButtonVariant.Primary}
            sx={narrowTabletButtonWidth}
            href={initiateBothFlowUrl}
          >
            {model?.buttonSupportForBoth}
          </Button>
        ) : null}
        <Button
          variant={ButtonVariant.Link}
          sx={narrowTabletButtonWidth}
          href={cancelUrl}
          onClick={cancelUrl ? undefined : () => window.history.back()}
        >
          {model?.cancelSupport}
        </Button>
      </div>
    </section>
  );
}

function DialogPage(props: Props) {
  const model = props;
  const { partyId } = usePartyId();
  const { electionType, isDefined: electionTypeIsDefined } = useElectionType();
  const { declarationType } = useDeclarationType();
  const { chosenElectionType } = useChosenElectionType();
  const { user, userReady } = useUser();
  const { loginUrl } = useAuth();
  const { t } = useDictionary();
  const { data: partiesResponse, status: partiesStatus } = useParties();
  const parties = partiesResponse?.items;
  const partyIds = partyId?.split(',') ?? [];
  const primaryPartyId = partyIds[0] || partyId;
  const shouldLoadPartyDetailsForInitiation =
    declarationType === DeclarationActionTypes.initiate &&
    electionType !== undefined &&
    !!primaryPartyId;
  const {
    data: partyDetails,
    status: partyDetailsStatus,
    isFetching: isFetchingPartyDetails,
  } = useParty(primaryPartyId, {
    enabled: shouldLoadPartyDetailsForInitiation,
    // Initiate dialogs are reached from login-time party/user state. Force a
    // fresh party lookup before routing or submitting so stale CTAs cannot send
    // obsolete actions.
    ...(shouldLoadPartyDetailsForInitiation ? { staleTime: 0 } : {}),
  });
  const currentParty: SchemaParty[] | undefined = parties?.filter(
    (p) => p.id === primaryPartyId,
  );
  const currentPartyName: string =
    currentParty?.[0]?.name || partyDetails?.name || '';
  const readyToConfirmDeclarations =
    user?.userDeclarations?.filter(
      (declaration) =>
        declaration.daysLeft === 0 &&
        !declaration.isConfirmed &&
        canConfirmUserDeclaration(declaration),
    ) ?? [];
  const dkDeclaration = readyToConfirmDeclarations.find(
    (declaration) =>
      declaration.electionType === ElectionType.Folketingsvalg &&
      (!partyIds.length || partyIds.includes(declaration.partyId || '')),
  );
  const euDeclaration = readyToConfirmDeclarations.find(
    (declaration) =>
      declaration.electionType === ElectionType.EuropaParlamentsvalg &&
      (!partyIds.length || partyIds.includes(declaration.partyId || '')),
  );
  const partyIdDk = dkDeclaration?.partyId;
  const partyIdEu = euDeclaration?.partyId;
  const getPartyNameById = (id?: string) => {
    if (!id) return '';

    const fromUserDeclaration = user?.userDeclarations?.find(
      (declaration) => declaration.partyId === id,
    )?.partyName;
    if (fromUserDeclaration) return fromUserDeclaration;

    return parties?.find((party) => party.id === id)?.name || '';
  };
  const dkPartyName = dkDeclaration?.partyName || getPartyNameById(partyIdDk);
  const euPartyName = euDeclaration?.partyName || getPartyNameById(partyIdEu);
  const existingDeclarationElectionType =
    user?.userDeclarations?.length === 1
      ? user.userDeclarations[0].electionType
      : undefined;

  // Hook for initiate buttons outside the two-collections flow
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorState, setErrorState] = useState<CollectionState | undefined>(
    undefined,
  );
  const [isInitiateSubmitting, setIsInitiateSubmitting] = useState(false);
  const { pathname: dialogPageUrl, searchParams } = useRouter();
  const {
    flowMode,
    nextInitiateReturnUrl,
    confirmReturnUrlOverride,
    withdrawnPartyId,
    withdrawnPartyName,
  } = getDialogQueryFlowContext({
    searchParams,
    declarationType,
    electionType,
    chosenElectionType,
    dialogPageUrl,
    partyId,
  });
  const [submitDeclaration, { status: submitStatus }] = useAddDeclaration({
    returnUrl: nextInitiateReturnUrl,
    // Once the first declaration has been created, that dialog is obsolete.
    // Keep browser Back from restoring the submitted step of the two-part flow.
    replaceReturnUrl: flowMode === DialogFlowMode.SupportBothSequence,
    onSettled: (data, error) => {
      if (!data || error) {
        setIsInitiateSubmitting(false);
        const stateCode =
          error?.details && 'stateCode' in error.details
            ? (error.details as { stateCode?: unknown }).stateCode
            : undefined;
        setErrorState(
          stateCode !== undefined && stateCode !== null
            ? (Number(stateCode) as CollectionState)
            : undefined,
        );
        setErrorModalOpen(true);
      }
    },
  });
  const { partyDetailsUrl } = usePartyDetailsUrl();
  const mustWaitForParties =
    declarationType === DeclarationActionTypes.initiate &&
    electionType === ElectionType.BothTypes &&
    chosenElectionType !== undefined &&
    partiesStatus !== 'success';
  const mustWaitForPartyDetails =
    !isInitiateSubmitting &&
    shouldLoadPartyDetailsForInitiation &&
    (partyDetailsStatus === 'loading' || isFetchingPartyDetails);
  const directActionElectionType = electionTypeIsDefined
    ? electionType
    : undefined;
  const isDirectDialogAction =
    declarationType !== undefined &&
    directActionElectionType !== undefined &&
    partyIds.length === 1;
  const directActionRedirectUrl = isDirectDialogAction
    ? buildPartyDetailsUrl(partyDetailsUrl, partyId, directActionElectionType)
    : undefined;

  useEffect(() => {
    if (userReady && !user) {
      redirectToUrl(directActionRedirectUrl || loginUrl || '/');
    }
  }, [directActionRedirectUrl, loginUrl, user, userReady]);

  if (!user || mustWaitForParties || mustWaitForPartyDetails)
    return <Placeholder css={{ height: 600 }} />;

  const flowState = computeDialogFlowState(
    declarationType,
    user,
    electionType,
    chosenElectionType,
    partyId,
    currentParty,
    {
      [ElectionType.Folketingsvalg]: partyDetails?.dk?.state as CollectionState,
      [ElectionType.EuropaParlamentsvalg]: partyDetails?.eu
        ?.state as CollectionState,
    },
    {
      hasExplicitElectionType: electionTypeIsDefined,
      mode: flowMode,
    },
  );
  const effectiveElectionType =
    flowState.selectedElectionType !== undefined
      ? flowState.selectedElectionType
      : electionType;
  const unknownFlowRedirectUrl =
    flowState.kind === DialogFlowKind.Unknown
      ? buildPartyDetailsUrl(partyDetailsUrl, partyId, effectiveElectionType)
      : undefined;

  if (flowState.kind === DialogFlowKind.Unknown) {
    return <RedirectToPartyPage url={unknownFlowRedirectUrl} />;
  }

  const modalErrorTextKey = getErrorWithState(
    errorState,
    DeclarationActionTypes.initiate,
    'errors.generic_text',
  );

  const bothFlowCancelUrl =
    flowMode === DialogFlowMode.SupportBothSequence ||
    flowState.kind === DialogFlowKind.InitiatePartyWithTwoCollections
      ? buildPartyDetailsUrl(partyDetailsUrl, partyId, effectiveElectionType)
      : undefined;
  const initiateCancelUrl =
    flowState.kind === DialogFlowKind.Initiate
      ? buildPartyDetailsUrl(partyDetailsUrl, partyId, effectiveElectionType)
      : undefined;
  const renderFlowState: DialogFlowState =
    isInitiateSubmitting && declarationType === DeclarationActionTypes.initiate
      ? {
          kind: DialogFlowKind.Initiate,
          selectedElectionType: effectiveElectionType,
        }
      : flowState;

  const dialogText = getDialogText(
    model,
    renderFlowState,
    effectiveElectionType,
    t,
    currentPartyName,
    withdrawnPartyName || getPartyNameById(withdrawnPartyId),
  );
  const button = getButton(
    model,
    renderFlowState,
    effectiveElectionType,
    partyId,
    partyDetailsUrl,
    dialogPageUrl,
    confirmReturnUrlOverride,
    flowMode === DialogFlowMode.SupportBothSequence,
  );
  const cancel = getCancelButton(
    model,
    renderFlowState,
    partyId,
    electionType,
    partyDetailsUrl,
  );
  const dialogImage = getDialogImage(model, renderFlowState);
  const dialogImageKey = [
    renderFlowState.kind,
    effectiveElectionType,
    dialogImage?.src,
  ].join(':');
  const signingAction = getSigningActionForFlow(renderFlowState);
  const signingPartyId =
    renderFlowState.declarationPartyIdToWithdraw ||
    renderFlowState.selectedPartyId ||
    primaryPartyId;

  return (
    <Container
      sx={{
        mb: rem(120),
        mt: [rem(40), rem(56), rem(120)],
      }}
    >
      <Grid
        gridGap={[rem(40), rem(48), rem(40)]}
        sx={{
          [narrowTabletDialogQuery]: {
            display: 'flex',
            gap: rem(32),
          },
        }}
      >
        <GridItem
          size={[1, 1, 6 / 12]}
          sx={{
            order: 1,
            [narrowTabletDialogQuery]: {
              flex: '1 1 0',
              maxWidth: 'none',
            },
          }}
        >
          <section
            sx={{
              maxWidth: [undefined, rem(560), 'none'],
              px: [rem(20), rem(20), rem(40)],
              [narrowTabletDialogQuery]: {
                maxWidth: 'none',
                pr: rem(16),
              },
            }}
          >
            <Heading
              as="h1"
              variant={TextVariant.HeaderXl}
              sx={{
                mb: [rem(24), rem(32)],
                fontSize: [rem(24), rem(32)],
                fontWeight: 400,
                lineHeight: [rem(32), rem(40)],
                [narrowTabletDialogQuery]: {
                  fontSize: rem(24),
                  lineHeight: rem(32),
                },
                ...readableHeadingTextWrap,
              }}
            >
              {dialogText?.title}
            </Heading>

            {renderFlowState.kind === DialogFlowKind.ConfirmTwoDeclarations ? (
              <ConfirmationSection
                dkPartyName={dkPartyName}
                euPartyName={euPartyName}
                partyIdDk={partyIdDk}
                partyIdEu={partyIdEu}
                model={model}
              />
            ) : (
              <RichText
                sx={{ mb: ['2rem', null, 8] }}
                variant={TextVariant.BodyMd}
              >
                {dialogText?.text}
              </RichText>
            )}

            {renderFlowState.kind ===
            DialogFlowKind.InitiatePartyWithTwoCollections ? (
              <SupportSection
                model={model}
                partyId={partyId || ''}
                existingDeclarationElectionType={
                  existingDeclarationElectionType
                }
                canSupportBoth={renderFlowState.canSupportBoth}
                cancelUrl={bothFlowCancelUrl}
              />
            ) : renderFlowState.kind === DialogFlowKind.Initiate ? (
              <StaleActionGuard
                action={DeclarationActionTypes.initiate}
                collectionState={collectionStateByElectionType(
                  partyDetails,
                  effectiveElectionType,
                )}
                electionType={effectiveElectionType}
                partyId={partyId}
              >
                {(unavailableState) => (
                  <ButtonsSection
                    button={
                      unavailableState === undefined ? (
                        <Button
                          variant={ButtonVariant.Primary}
                          sx={narrowTabletButtonWidth}
                          disabled={
                            isInitiateSubmitting || submitStatus === 'loading'
                          }
                          submitting={
                            isInitiateSubmitting || submitStatus === 'loading'
                          }
                          onClick={() => {
                            setIsInitiateSubmitting(true);
                            submitDeclaration({
                              partyId: partyId || '',
                              electionType: effectiveElectionType as ElectionType,
                            });
                          }}
                        >
                          {model?.buttonInitiate}
                        </Button>
                      ) : null
                    }
                    cancel={
                      model?.cancelButton ? (
                        <Button
                          variant={ButtonVariant.Link}
                          sx={narrowTabletButtonWidth}
                          href={initiateCancelUrl}
                          onClick={
                            initiateCancelUrl
                              ? undefined
                              : () => window.history.back()
                          }
                        >
                          {model?.cancelButton}
                        </Button>
                      ) : null
                    }
                  />
                )}
              </StaleActionGuard>
            ) : (
              <StaleActionGuard
                action={signingAction}
                electionType={effectiveElectionType}
                partyId={signingPartyId}
                userDeclarations={user.userDeclarations}
              >
                {(unavailableState) => (
                  <ButtonsSection
                    button={unavailableState === undefined ? button : null}
                    cancel={cancel}
                    stacked={
                      renderFlowState.kind ===
                      DialogFlowKind.InitiateBothTypesUserHasDeclaration
                    }
                  />
                )}
              </StaleActionGuard>
            )}
          </section>
        </GridItem>
        <GridItem
          size={[1, 1, 6 / 12]}
          sx={{
            order: 2,
            [narrowTabletDialogQuery]: {
              flex: '1 1 0',
              maxWidth: 'none',
            },
          }}
        >
          <div
            sx={{
              px: [rem(20), rem(20), 0],
              [narrowTabletDialogQuery]: {
                pl: rem(16),
              },
            }}
          >
            <div
              sx={{
                maxWidth: [undefined, rem(520), 'none'],
                [narrowTabletDialogQuery]: {
                  maxWidth: 'none',
                },
              }}
            >
              <Image
                key={dialogImageKey}
                {...dialogImage}
                src={dialogImage?.src || ''}
                sizes={['100vw']}
                css={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  borderRadius: rem(18),
                }}
              />
            </div>
          </div>
        </GridItem>
      </Grid>

      <Modal
        isOpen={errorModalOpen}
        onDismiss={() => setErrorModalOpen(false)}
        title={t('errors.generic_title', { asString: true })}
      >
        <RichText>{t(modalErrorTextKey)}</RichText>
      </Modal>
    </Container>
  );
}

DialogPage.displayName = 'DialogPage';

export default DialogPage;
